import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'app-redux'
import { selectCurrentScriptStep } from 'features/script/script-redux'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
	MiscSelectedSelectedItem,
	setMiscPanelOpen,
	setMiscPanelSelectedItem,
} from 'room/common/redux'
import { useExitSession } from 'room/common/use-exit-session'
import { ConfiguredPanel } from './configured-panel'
import {
	FeedButton,
	MenuSheet,
	MenuTarget,
	TopBar,
} from './presentation/chrome'
import { UnreadCounts, UnreadCountsSource } from './unread-counts'
import { useVisualViewport } from './use-visual-viewport'

const Frame = styled.div`
	position: fixed;
	left: 0;
	right: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	background: ${colors.athensGray};
	color: ${colors.blackish};
`

/** Everything under the top bar; overlays position against it. */
const Main = styled.div`
	position: relative;
	flex: 1;
	min-height: 0;
`

const Body = styled.main`
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
`

const MENU_TARGETS: Record<
	Exclude<MenuTarget, 'exit'>,
	MiscSelectedSelectedItem
> = {
	chat: MiscSelectedSelectedItem.Messages,
	agenda: MiscSelectedSelectedItem.Agenda,
	feed: MiscSelectedSelectedItem.Boardlet,
}

type Props = {
	/** The LIVE marker and the current agenda item; off outside the room. */
	live: boolean
	testId?: string
	children: ReactNode
}

/**
 * The participant's phone and tablet frame for an in-person session: a top
 * bar with the training and the current agenda item, the stage, a hamburger
 * menu (chat, boardlet, agenda, leave) and the floating boardlet button.
 *
 * Chat, agenda and boardlet open over the stage and reuse the desktop
 * `room.miscPanel` state. That state outlives this frame (a break, a tablet
 * rotated across 1024px), so the frame starts with the panel closed: the
 * participant lands on what is happening, not on a chat they opened earlier.
 */
export const MobileFrame = ({ live, testId, children }: Props): JSX.Element => {
	const dispatch = useDispatch()
	const confirm = ui.useConfirm()
	const viewport = useVisualViewport()
	const training = useSelector(state => state.sessionInfo.training)
	const module = useSelector(
		state => selectCurrentScriptStep(state)?.item.title
	)
	const panelOpen = useSelector(state => state.room.miscPanel.isOpen)
	const selected = useSelector(state => state.room.miscPanel.selectedItem)
	const exit = useExitSession(training?.upperEchelon.userId)

	const [menuOpen, setMenuOpen] = useState(false)
	const [unread, setUnread] = useState<UnreadCounts>({ chat: 0, feed: 0 })
	const menuButtonRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		dispatch(setMiscPanelOpen(false))
	}, [dispatch])

	const openPanel = useCallback(
		(item: MiscSelectedSelectedItem) => {
			dispatch(setMiscPanelSelectedItem(item))
			dispatch(setMiscPanelOpen(true))
		},
		[dispatch]
	)

	const askToLeave = () =>
		confirm
			.generic(
				<intl.Translate>in_person.mobile.exit.title</intl.Translate>,
				<intl.Translate>in_person.mobile.exit.description</intl.Translate>,
				<intl.Translate>in_person.mobile.exit.confirm</intl.Translate>,
				<intl.Translate>in_person.mobile.exit.stay</intl.Translate>,
				{ destructive: true }
			)
			// Staying is what cancelling means - not an error.
			.then(exit, () => undefined)

	const pick = (target: MenuTarget) => {
		setMenuOpen(false)
		if (target === 'exit') {
			askToLeave()
		} else {
			openPanel(MENU_TARGETS[target])
		}
	}

	const closeMenu = useCallback(() => setMenuOpen(false), [])

	return (
		<Frame
			style={{ top: viewport.offsetTop, height: viewport.height }}
			data-testid={testId}
		>
			<TopBar
				training={training?.subject}
				module={live ? module : undefined}
				live={live}
				hasUnread={unread.chat + unread.feed > 0}
				menuOpen={menuOpen}
				menuButtonRef={menuButtonRef}
				onMenu={() => setMenuOpen(open => !open)}
			/>
			<Main>
				<Body>{children}</Body>
				{panelOpen ? <ConfiguredPanel /> : null}
				{training?.feedType && !panelOpen ? (
					<FeedButton
						feedType={training.feedType}
						count={unread.feed}
						onClick={() => openPanel(MiscSelectedSelectedItem.Boardlet)}
					/>
				) : null}
				{menuOpen ? (
					<MenuSheet
						feedType={training?.feedType}
						unread={unread}
						returnFocusTo={menuButtonRef}
						onPick={pick}
						onClose={closeMenu}
					/>
				) : null}
			</Main>
			{training ? (
				<UnreadCountsSource
					trainingId={training.id}
					chatOpen={panelOpen && selected === MiscSelectedSelectedItem.Messages}
					feedOpen={panelOpen && selected === MiscSelectedSelectedItem.Boardlet}
					onChange={setUnread}
				/>
			) : null}
		</Frame>
	)
}

export default MobileFrame
